import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { OrderImageRecord } from '../../../services/orderImagesService';
import { useLineItemImages } from '../../../hooks/useLineItemImages';
import { ShippingLineItem } from '../../../DeShippingTableWrapper';
import { useLineItems } from '../../../hooks/useLineItems';

interface ImageGridProps {
  lineItemsHook: ReturnType<typeof useLineItems>;
  lineItemImagesHook: ReturnType<typeof useLineItemImages>;
}

export const ImageGrid: React.FC<ImageGridProps> = ({ 
  lineItemsHook,
  lineItemImagesHook,
}) => {
  const [selectedImage, setSelectedImage] = useState<OrderImageRecord | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<OrderImageRecord | null>(null);

  const handleImageClick = (image: OrderImageRecord) => {
    setSelectedImage(image);
    setDialogVisible(true);
  };

  const closeDialog = () => {
    setDialogVisible(false);
    setSelectedImage(null);
  };

  const handleDeleteClick = (e: React.MouseEvent, image: OrderImageRecord) => {
    e.stopPropagation();
    setImageToDelete(image);
    setDeleteConfirmVisible(true);
  };

  const confirmDelete = async () => {
    if (imageToDelete) {
      try {
        await lineItemImagesHook.deleteImage(imageToDelete.id);
        setDeleteConfirmVisible(false);
        setImageToDelete(null);
      } catch (error) {
        // Error is already handled in the hook
        console.error('Delete failed:', error);
      }
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmVisible(false);
    setImageToDelete(null);
  };

  return (
    <div className="flex-1 flex flex-column">
      {/* Image Grid */}
      <div className="flex-1 overflow-auto">
        {/* 1st Priority: Loading state */}
        {(lineItemsHook.loading || lineItemImagesHook.isLoading) ? (
          <div className="thumbnails-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="thumbnail-container">
                <Skeleton size="12rem" />
              </div>
            ))}
          </div>
        ) : /* 2nd Priority: No line item selected */ 
        !lineItemsHook.selectedLineItem ? (
          <div className="h-full flex align-items-center justify-content-center">
            <div className="text-center p-4">
              <i className="pi pi-eye text-6xl text-400 mb-3"></i>
              <h4 className="text-700 mb-2">Select a Line Item for Review</h4>
              <p className="text-500 m-0">Choose a line item from the table to view and manage its images</p>
            </div>
          </div>
        ) : /* 3rd Priority: Line item selected but no images */
        lineItemImagesHook.selectedLineItemImages.length === 0 ? (
          <div className="h-full flex align-items-center justify-content-center">
            <div className="text-center p-4">
              <i className="pi pi-image text-6xl text-400 mb-3"></i>
              <h4 className="text-700 mb-2">No Images Available</h4>
              <p className="text-500 m-0">No images have been uploaded for this line item</p>
            </div>
          </div>
        ) : /* 4th Priority: Show images */
        (
          <div className="thumbnails-grid">
            {lineItemImagesHook.selectedLineItemImages.map((image) => {
              const isDeleting = lineItemImagesHook.deletingImages.has(image.id);
              
              return (
                <div
                  key={image.id}
                  className={`thumbnail-container ${isDeleting ? 'opacity-50' : ''}`}
                  onClick={() => !isDeleting && handleImageClick(image)}
                  style={{ cursor: isDeleting ? 'not-allowed' : 'pointer' }}
                >
                  <img
                    src={image.image_url}
                    alt={image.filename}
                    className="thumbnail-image"
                  />
                  <Button 
                    icon={isDeleting ? "pi pi-spin pi-spinner" : "pi pi-times"}
                    className="remove-button"
                    onClick={(e) => handleDeleteClick(e, image)}
                    severity="danger"
                    disabled={isDeleting}
                    loading={isDeleting}
                  />
                  <div className="image-filename">
                    {image.filename}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Full Size Image Dialog */}
      <Dialog
        visible={dialogVisible}
        onHide={closeDialog}
        header={selectedImage?.filename}
        style={{ width: '80vw', maxWidth: '800px' }}
        modal
        dismissableMask
      >
        {selectedImage && (
          <div className="flex justify-content-center">
            <img
              src={selectedImage.image_url}
              alt={selectedImage.filename}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '70vh', 
                objectFit: 'contain' 
              }}
            />
          </div>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={deleteConfirmVisible}
        onHide={cancelDelete}
        header="Confirm Delete"
        style={{ width: '400px' }}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button 
              label="Cancel" 
              icon="pi pi-times" 
              onClick={cancelDelete}
              className="p-button-text"
            />
            <Button 
              label="Delete" 
              icon="pi pi-trash" 
              onClick={confirmDelete}
              severity="danger"
            />
          </div>
        }
      >
        <div className="flex align-items-center gap-3">
          <i className="pi pi-exclamation-triangle text-3xl text-orange-500"></i>
          <div>
            <p className="m-0 mb-2">Are you sure you want to delete this image?</p>
            {imageToDelete && (
              <p className="m-0 text-sm text-500">
                <strong>File:</strong> {imageToDelete.filename}
              </p>
            )}
          </div>
        </div>
      </Dialog>

      {/* Error Display */}
      {lineItemImagesHook.error && (
        <div className="p-3 bg-red-50 border-1 border-red-200 border-round mt-2">
          <div className="flex align-items-center gap-2 text-red-700">
            <i className="pi pi-exclamation-circle"></i>
            <span className="text-sm">{lineItemImagesHook.error}</span>
          </div>
        </div>
      )}
    </div>
  );
};